import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Avatar,
  AvatarGroup,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tabs,
  Tab,
  Badge,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import { collectionsAPI, outfitsAPI, wardrobeAPI } from '../services/api';
import { OutfitCollection, Outfit, WardrobeItem } from '../types';
import { useUI } from '../contexts/UIContext';

const Collections: React.FC = () => {
  const { toast, confirm } = useUI();
  const navigate = useNavigate();
  const [collections, setCollections] = useState<OutfitCollection[]>([]);
  const [outfits, setOutfits] = useState<Outfit[]>([]);
  const [wardrobeItems, setWardrobeItems] = useState<WardrobeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<OutfitCollection | null>(null);
  const [, setOutfitSelectorOpen] = useState(false);
  const [viewingCollection, setViewingCollection] = useState<OutfitCollection | null>(null);
  const [selectionTab, setSelectionTab] = useState<'outfits' | 'items'>('outfits');

  // Form state
  const [collectionName, setCollectionName] = useState('');
  const [collectionDescription, setCollectionDescription] = useState('');
  const [collectionType, setCollectionType] = useState('');
  const [collectionTags, setCollectionTags] = useState<string[]>([]);
  const [selectedOutfitIds, setSelectedOutfitIds] = useState<number[]>([]);
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [collectionsRes, outfitsRes, itemsRes] = await Promise.all([
        collectionsAPI.getCollections(),
        outfitsAPI.getOutfits(),
        wardrobeAPI.getItems(),
      ]);

      setCollections(collectionsRes.data);
      setOutfits(outfitsRes.data);
      setWardrobeItems(itemsRes.data);
    } catch (error) {
      console.error('Error fetching collections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCollection = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleEditCollection = (collection: OutfitCollection) => {
    setSelectedCollection(collection);
    setCollectionName(collection.name);
    setCollectionDescription(collection.description || '');
    setCollectionType(collection.collection_type || '');
    setCollectionTags(collection.tags || []);
    setSelectedOutfitIds(collection.outfit_ids || []);
    setSelectedItemIds(collection.item_ids || []);
    setDialogOpen(true);
  };

  const handleSaveCollection = async () => {
    try {
      const collectionData = {
        name: collectionName,
        description: collectionDescription,
        collection_type: collectionType,
        tags: collectionTags,
        outfit_ids: selectedOutfitIds,
        item_ids: selectedItemIds,
      };

      if (selectedCollection) {
        await collectionsAPI.updateCollection(selectedCollection.id, collectionData);
      } else {
        await collectionsAPI.createCollection(collectionData);
      }

      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving collection:', error);
    }
  };

  const handleDeleteCollection = async (collectionId: number) => {
    const ok = await confirm({
      title: 'Delete this collection?',
      message: 'The collection will be removed. The outfits inside it stay in your library.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await collectionsAPI.deleteCollection(collectionId);
      fetchData();
      toast('Collection deleted', 'success');
    } catch (error) {
      console.error('Error deleting collection:', error);
      toast('Failed to delete collection', 'error');
    }
  };

  const handleAddTag = () => {
    if (newTag && !collectionTags.includes(newTag)) {
      setCollectionTags([...collectionTags, newTag]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setCollectionTags(collectionTags.filter(tag => tag !== tagToRemove));
  };

  const toggleOutfitSelection = (outfitId: number) => {
    setSelectedOutfitIds(prev =>
      prev.includes(outfitId)
        ? prev.filter(id => id !== outfitId)
        : [...prev, outfitId]
    );
  };

  const toggleItemSelection = (itemId: number) => {
    setSelectedItemIds(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const resetForm = () => {
    setSelectedCollection(null);
    setCollectionName('');
    setCollectionDescription('');
    setCollectionType('');
    setCollectionTags([]);
    setSelectedOutfitIds([]);
    setSelectedItemIds([]);
    setNewTag('');
    setSelectionTab('outfits');
  };

  const getOutfitDetails = (outfitIds: number[]) => {
    return outfits.filter(outfit => outfitIds.includes(outfit.id));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Outfit Collections</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateCollection}
        >
          Create Collection
        </Button>
      </Box>

      <Grid container spacing={3}>
        {collections.map((collection) => {
          const collectionOutfits = getOutfitDetails(collection.outfit_ids || []);
          
          return (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={collection.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" component="div">
                      {collection.name}
                    </Typography>
                  </Box>
                  
                  {collection.description && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {collection.description}
                    </Typography>
                  )}
                  
                  {collection.collection_type && (
                    <Chip
                      label={collection.collection_type}
                      size="small"
                      color="primary"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  )}
                  
                  {collection.tags && collection.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      {collectionOutfits.length} Outfits
                    </Typography>
                    <AvatarGroup max={4} sx={{ justifyContent: 'flex-start' }}>
                      {collectionOutfits.slice(0, 4).map((outfit) => (
                        <Avatar key={outfit.id} sx={{ width: 32, height: 32 }}>
                          {outfit.name.charAt(0)}
                        </Avatar>
                      ))}
                    </AvatarGroup>
                  </Box>
                </CardContent>
                
                <CardActions>
                  <Button
                    size="small"
                    onClick={() => setViewingCollection(collection)}
                  >
                    View Outfits
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => handleEditCollection(collection)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteCollection(collection.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Create/Edit Collection Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedCollection ? 'Edit Collection' : 'Create New Collection'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Collection Name"
              value={collectionName}
              onChange={(e) => setCollectionName(e.target.value)}
              fullWidth
              required
            />
            
            <TextField
              label="Description"
              value={collectionDescription}
              onChange={(e) => setCollectionDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
            
            <FormControl fullWidth>
              <InputLabel>Collection Type</InputLabel>
              <Select
                value={collectionType}
                label="Collection Type"
                onChange={(e) => setCollectionType(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="seasonal">Seasonal</MenuItem>
                <MenuItem value="vacation">Vacation</MenuItem>
                <MenuItem value="work">Work</MenuItem>
                <MenuItem value="weekend">Weekend</MenuItem>
                <MenuItem value="special_events">Special Events</MenuItem>
                <MenuItem value="capsule">Capsule Wardrobe</MenuItem>
              </Select>
            </FormControl>
            
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Tags
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  placeholder="Add tag"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                />
                <Button onClick={handleAddTag}>Add</Button>
              </Box>
              <Box>
                {collectionTags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                ))}
              </Box>
            </Box>
            
            <Box>
              <Tabs
                value={selectionTab}
                onChange={(_, newValue) => setSelectionTab(newValue)}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              >
                <Tab
                  label={
                    <Badge badgeContent={selectedOutfitIds.length} color="primary">
                      Outfits
                    </Badge>
                  }
                  value="outfits"
                />
                <Tab
                  label={
                    <Badge badgeContent={selectedItemIds.length} color="primary">
                      Individual Items
                    </Badge>
                  }
                  value="items"
                />
              </Tabs>

              {selectionTab === 'outfits' && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Select complete outfits to add to this collection
                  </Typography>
                  {loading ? (
                    <Box
                      sx={{
                        p: 4,
                        textAlign: 'center',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                      }}
                    >
                      <CircularProgress size={32} />
                    </Box>
                  ) : outfits.length === 0 ? (
                    <Box
                      sx={{
                        p: 4,
                        textAlign: 'center',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        bgcolor: 'grey.50',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No outfits available. Create some outfits first to add them to collections.
                      </Typography>
                    </Box>
                  ) : (
                    <List sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      {outfits.map((outfit) => (
                        <ListItemButton
                          key={outfit.id}
                          onClick={() => toggleOutfitSelection(outfit.id)}
                          selected={selectedOutfitIds.includes(outfit.id)}
                        >
                          <ListItemAvatar>
                            <Avatar>
                              {outfit.name.charAt(0)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={outfit.name}
                            secondary={`${outfit.occasion || 'No occasion'} - ${outfit.items?.length || 0} items`}
                          />
                          <ListItemSecondaryAction>
                            <Chip
                              label={selectedOutfitIds.includes(outfit.id) ? 'Selected' : 'Select'}
                              color={selectedOutfitIds.includes(outfit.id) ? 'primary' : 'default'}
                              size="small"
                            />
                          </ListItemSecondaryAction>
                        </ListItemButton>
                      ))}
                    </List>
                  )}
                </Box>
              )}

              {selectionTab === 'items' && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Select individual wardrobe items to add to this collection
                  </Typography>
                  {loading ? (
                    <Box
                      sx={{
                        p: 4,
                        textAlign: 'center',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                      }}
                    >
                      <CircularProgress size={32} />
                    </Box>
                  ) : wardrobeItems.length === 0 ? (
                    <Box
                      sx={{
                        p: 4,
                        textAlign: 'center',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        bgcolor: 'grey.50',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No wardrobe items available. Add some items to your wardrobe first.
                      </Typography>
                    </Box>
                  ) : (
                    <List sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      {wardrobeItems.map((item) => (
                        <ListItemButton
                          key={item.id}
                          onClick={() => toggleItemSelection(item.id)}
                          selected={selectedItemIds.includes(item.id)}
                        >
                          <ListItemAvatar>
                            <Avatar
                              variant="rounded"
                              src={item.image_url}
                              sx={{ width: 48, height: 48 }}
                            >
                              {item.category?.[0] || '?'}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={item.name || `${item.category} - ${item.brand || 'Unknown'}`}
                            secondary={`${item.category || 'Unknown'} | ${item.colors?.join(', ') || 'No colors'}`}
                          />
                          <ListItemSecondaryAction>
                            <Chip
                              label={selectedItemIds.includes(item.id) ? 'Selected' : 'Select'}
                              color={selectedItemIds.includes(item.id) ? 'primary' : 'default'}
                              size="small"
                            />
                          </ListItemSecondaryAction>
                        </ListItemButton>
                      ))}
                    </List>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveCollection}
            variant="contained"
            disabled={!collectionName}
          >
            {selectedCollection ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View the outfits inside a collection */}
      <Dialog open={!!viewingCollection} onClose={() => setViewingCollection(null)} maxWidth="md" fullWidth>
        <DialogTitle>{viewingCollection?.name || 'Collection'}</DialogTitle>
        <DialogContent>
          {viewingCollection && (() => {
            const outfitsInCollection = getOutfitDetails(viewingCollection.outfit_ids || []);
            if (outfitsInCollection.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  This collection has no outfits yet. Use “Edit” to add some.
                </Typography>
              );
            }
            return (
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                {outfitsInCollection.map((outfit) => (
                  <Grid size={{ xs: 6, sm: 4 }} key={outfit.id}>
                    <Card
                      variant="outlined"
                      sx={{ cursor: 'pointer', height: '100%' }}
                      onClick={() => navigate(`/outfits/${outfit.id}`)}
                    >
                      {outfit.thumbnail_url ? (
                        <CardMedia
                          component="img"
                          image={outfit.thumbnail_url}
                          alt={outfit.name}
                          sx={{ height: 150, objectFit: 'cover', objectPosition: 'center top', bgcolor: '#f5f5f5' }}
                        />
                      ) : (
                        <Box sx={{ height: 150, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <FolderIcon color="disabled" />
                        </Box>
                      )}
                      <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{outfit.name}</Typography>
                        {outfit.occasion && (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {outfit.occasion}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewingCollection(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Collections;